import React, { useMemo } from "react"
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import Checkbox from "../ui/Checkbox"
import { cn } from "@/lib/utils"

const SHELL =
  "overflow-hidden rounded-xl border border-subtle bg-card shadow-sm max-md:rounded-none max-md:border-x-0 max-md:shadow-none"
const MOBILE_TABLE =
  "max-md:[&_tbody]:flex max-md:[&_tbody]:flex-col max-md:[&_tbody]:gap-3 max-md:[&_tbody]:p-3 max-md:[&_tr]:relative max-md:[&_tr]:flex max-md:[&_tr]:flex-col max-md:[&_tr]:overflow-hidden max-md:[&_tr]:rounded-xl max-md:[&_tr]:border max-md:[&_tr]:border-subtle max-md:[&_tr]:bg-card max-md:[&_tr]:shadow-sm max-md:[&_tr]:border-b-0"
const CHECKBOX_COL = "w-12 px-3 text-center"
const HEAD_CELL = "table-head-cell select-none"
const MOBILE_LABEL = "mb-1 block text-[11px] font-medium uppercase tracking-wider text-secondary md:hidden"

type Align = "left" | "center" | "right"

const isUtility = (id: string) => id === "select" || id === "actions"
const cellAlign = (align?: string) =>
  align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"
const metaAlign = (def: unknown) => (def as { meta?: { align?: Align } }).meta?.align
const metaLabel = (def: unknown) => (def as { meta?: { label?: string } }).meta?.label
const cellLabel = (id: string, header: unknown, label?: string) =>
  isUtility(id) ? "" : label || (typeof header === "string" ? header : id)

const checkboxBox = (checked: boolean, onChange: () => void, indeterminate?: boolean) => (
  <div className="flex items-center justify-center">
    <Checkbox checked={checked} indeterminate={indeterminate} onChange={onChange} checkboxSize="sm" />
  </div>
)

const mobileCellClass = (id: string, isPrimary: boolean, hasSelect: boolean, align?: string) => {
  const base = cn(
    "py-2.5 px-3 text-sm md:px-6 md:py-4",
    cellAlign(align),
    isPrimary ? "table-cell-primary" : "table-cell-data",
  )
  if (id === "select") {
    return cn(
      base,
      CHECKBOX_COL,
      "max-md:flex max-md:w-auto max-md:items-center max-md:border-b max-md:border-subtle max-md:bg-surface-subtle max-md:px-s3 max-md:py-s2 max-md:[&>div]:justify-start",
    )
  }
  if (id === "actions") {
    return cn(
      base,
      "max-md:static max-md:flex max-md:w-full max-md:items-center max-md:justify-end max-md:gap-s1 max-md:border-0 max-md:bg-surface-subtle max-md:px-s2 max-md:py-s2",
    )
  }
  return cn(
    base,
    "max-md:block max-md:border-b max-md:border-subtle max-md:px-s4 max-md:py-s2 max-md:text-left",
    isPrimary && cn("max-md:pb-3", hasSelect ? "max-md:pt-s3" : "max-md:pt-s4"),
  )
}

const renderDataCell = (id: string, primary: boolean, label: string, content: React.ReactNode) => {
  if (isUtility(id)) return content
  return (
    <>
      {!primary && label ? <span className={MOBILE_LABEL}>{label}</span> : null}
      <div className={cn("min-w-0 break-words md:contents", primary ? "table-cell-primary" : "table-cell-data")}>
        {content}
      </div>
    </>
  )
}

export interface Column<T> {
  key: string
  header: string | React.ReactNode
  /** Rótulo mobile quando `header` não é string. */
  label?: string
  align?: Align
  render: (item: T, index: number) => React.ReactNode
}

interface DataTableProps<TData, TValue> {
  columns: (ColumnDef<TData, TValue> | Column<TData>)[]
  data: TData[]
  getRowKey?: (item: TData) => string
  emptyMessage?: string | React.ReactNode
  isLoading?: boolean
  selectable?: boolean
  selectedIds?: Set<string>
  onToggleSelection?: (id: string) => void
  onToggleAll?: () => void
  allSelected?: boolean
  someSelected?: boolean
  getRowClassName?: (item: TData) => string
  footer?: React.ReactNode
  title?: string
  /** Sem borda/título — usar dentro de um card pai (ex.: dashboard). */
  embedded?: boolean
}

export default function DataTable<TData, TValue>({
  columns: inputColumns,
  data,
  getRowKey,
  emptyMessage = "Nenhum resultado encontrado.",
  isLoading = false,
  selectable = false,
  selectedIds,
  onToggleSelection,
  onToggleAll,
  allSelected,
  someSelected,
  getRowClassName,
  footer,
  title,
  embedded = false,
}: DataTableProps<TData, TValue>) {
  const getKey = (item: TData) => (getRowKey ? getRowKey(item) : (item as { id: string }).id)

  const columns = useMemo(() => {
    const normalized = inputColumns.map((col) => {
      if ("render" in col && "key" in col) {
        const legacy = col as Column<TData>
        return {
          id: legacy.key,
          header: legacy.header,
          cell: ({ row }: { row: { original: TData; index: number } }) =>
            legacy.render(row.original, row.index),
          meta: { align: legacy.align, label: legacy.label },
        } as ColumnDef<TData, TValue>
      }
      return col as ColumnDef<TData, TValue>
    })

    if (!selectable) return normalized

    return [
      {
        id: "select",
        header: () => checkboxBox(!!allSelected, onToggleAll || (() => {}), someSelected),
        cell: ({ row }: { row: { original: TData } }) => {
          const key = getKey(row.original)
          return checkboxBox(!!selectedIds?.has(key), () => onToggleSelection?.(key))
        },
        size: 40,
      } as ColumnDef<TData, TValue>,
      ...normalized,
    ]
  }, [inputColumns, selectable, allSelected, someSelected, selectedIds, onToggleAll, onToggleSelection])

  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() })
  const headers = table.getHeaderGroups()[0]?.headers ?? []
  const rows = table.getRowModel().rows

  return (
    <div className={embedded ? "min-w-0" : SHELL}>
      {title && !embedded && (
        <div className="border-b border-subtle px-s4 py-s3 md:px-s5">
          <h2 className="font-sans text-md font-semibold tracking-normal text-default">{title}</h2>
        </div>
      )}

      <Table className={MOBILE_TABLE}>
        <TableHeader className="max-md:hidden md:overflow-hidden md:rounded-t-xl">
          <TableRow className="border-0 hover:bg-transparent">
            {isLoading
              ? columns.map((_, i) => (
                  <TableHead key={i} className="h-12 w-32 px-4">
                    <div className="h-2.5 w-full animate-pulse rounded bg-muted/10" />
                  </TableHead>
                ))
              : headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={cn(HEAD_CELL, header.id === "select" && CHECKBOX_COL, cellAlign(metaAlign(header.column.columnDef)))}
                  >
                    {!header.isPlaceholder && flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading ? (
            Array.from({ length: 5 }, (_, i) => (
              <TableRow key={i} className="border-border">
                {columns.map((_, j) => (
                  <TableCell key={j} className="px-4 py-4">
                    <div className="h-4 w-full animate-pulse rounded bg-muted/5" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length ? (
            rows.map((row) => {
              const key = getKey(row.original)
              const cells = row.getVisibleCells()
              const hasSelect = cells.some((c) => c.column.id === "select")
              const primaryIndex = cells.findIndex((c) => !isUtility(c.column.id))

              return (
                <TableRow
                  key={row.id}
                  data-state={selectedIds?.has(key) ? "selected" : undefined}
                  className={cn("max-md:border-0 max-md:hover:bg-transparent", getRowClassName?.(row.original))}
                >
                  {cells.map((cell, cellIndex) => {
                    const id = cell.column.id
                    const def = cell.column.columnDef
                    const content = flexRender(def.cell, cell.getContext())
                    return (
                      <TableCell
                        key={cell.id}
                        className={mobileCellClass(id, cellIndex === primaryIndex, hasSelect, metaAlign(def))}
                      >
                        {renderDataCell(id, cellIndex === primaryIndex, cellLabel(id, def.header, metaLabel(def)), content)}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-32 px-4 text-center text-sm italic text-muted max-md:block max-md:h-auto max-md:py-10"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {footer && <div className="table-footer shrink-0 md:rounded-b-xl">{footer}</div>}
    </div>
  )
}
